import useCanvas from '@canvas-builder/composables/useCanvas'
import usePrintMode from '@canvas-builder/composables/usePrintMode'
import useThemeVars from '@lib/composables/useThemeVars'

export default function useDataTablePrint() {
  const { isPrintDisplayMode } = useCanvas()
  const { resolveAllCssVariables } = usePrintMode()
  const themeVars = useThemeVars()

  const PRINT_STYLE_RENDERING_DELAY_MS = 100

  const applyHeaderPrintStyles = (
    headerElement: HTMLElement | null,
    headerBackgroundColor: string,
    headerForegroundColor: string,
  ) => {
    if (!headerElement || !isPrintDisplayMode.value) return

    setTimeout(() => {
      if (headerElement) {
        // Force the browser to compute critical styles before applying print transformations
        const style = window.getComputedStyle(headerElement)
        ;['color', 'backgroundColor', 'fontFamily'].forEach((prop) => style[prop as any])

        // Process any remaining CSS variables directly in the DOM
        resolveAllCssVariables(headerElement)

        // Apply direct styles to all table header cells for print mode
        const headerCells = headerElement.querySelectorAll('th')
        headerCells.forEach((cell) => {
          const cellEl = cell as HTMLElement
          // Apply the background-color directly from computed style
          const computedStyle = window.getComputedStyle(cellEl)
          if (computedStyle.backgroundColor.includes('var(--')) {
            cellEl.style.backgroundColor = headerBackgroundColor
          }

          // Apply to inner div elements too
          const divElements = cellEl.querySelectorAll('div')
          divElements.forEach((div) => {
            const divEl = div as HTMLElement
            const divStyle = window.getComputedStyle(divEl)

            if (divStyle.backgroundColor.includes('var(--')) {
              divEl.style.backgroundColor = headerBackgroundColor
            }

            if (divStyle.color.includes('var(--')) {
              divEl.style.color = headerForegroundColor
            }
          })
        })
      }
    }, PRINT_STYLE_RENDERING_DELAY_MS)
  }

  const applyRowPrintStyles = (container: HTMLElement | null) => {
    if (!container || !isPrintDisplayMode.value) return

    setTimeout(() => {
      if (container) {
        // Apply direct styling to table rows
        const dataRows = container.querySelectorAll('.data-row, tbody tr')
        dataRows.forEach((row, idx) => {
          const rowEl = row as HTMLElement
          rowEl.style.borderBottom = `1px solid ${themeVars.value.primary6}`
          rowEl.style.backgroundColor = idx % 2 ? themeVars.value.primary6_1 : themeVars.value.base

          // Fix cell borders
          const cells = rowEl.querySelectorAll('td')
          cells.forEach((cell) => {
            const cellEl = cell as HTMLElement
            cellEl.style.borderBottom = `1px solid ${themeVars.value.primary6}`
          })
        })
      }
    }, PRINT_STYLE_RENDERING_DELAY_MS)
  }

  const applyPropertyCellPrintStyles = (
    container: HTMLElement | null,
    headerBackgroundColor: string,
    headerForegroundColor: string,
  ) => {
    if (!container || !isPrintDisplayMode.value) return

    setTimeout(() => {
      if (container) {
        // Apply direct styling to property cells in row layout
        const propertyCells = container.querySelectorAll('[data-property-cell="true"]')
        propertyCells.forEach((cell) => {
          const cellEl = cell as HTMLElement
          cellEl.style.backgroundColor = headerBackgroundColor
          cellEl.style.color = headerForegroundColor
          cellEl.style.fontWeight = 'bold'
        })
      }
    }, PRINT_STYLE_RENDERING_DELAY_MS)
  }

  return {
    applyHeaderPrintStyles,
    applyRowPrintStyles,
    applyPropertyCellPrintStyles,
  }
}
